/**
 * Design tokens — the single source of truth for the look.
 *
 * Direction: "midnight card room". A premium late-night table, not a candy
 * casual game. The felt is deep plum-black so that saturation is spent only on
 * money (gold), the wild (violet), and win/loss states. Light is warm, low and
 * raking, so cards cast long soft shadows and read as physical objects.
 *
 * Every agent derives colour from here. Do not hardcode hex values elsewhere.
 */

export const palette = {
  /** Page / clear colour, deepest value in the scene. */
  void: 0x0d0a18,
  /** Felt base. */
  felt: 0x1b1030,
  /** Felt in shadow, used for the vignette falloff and pile wells. */
  feltDeep: 0x120a20,
  /** Raised surfaces, rails, hairlines. */
  plum: 0x2a1a4a,

  /** Money, stakes, winnings. Reserved — never decorative. */
  gold: 0xffc53d,
  goldDeep: 0xc98a12,
  /** The wild card, and the single primary call to action. */
  violet: 0xb401e0,
  violetLight: 0xd873ff,
  /** Exactly 21. */
  mint: 0x34d399,
  /** Bust. */
  rose: 0xf43f5e,

  /**
   * In-match levels, and nothing else.
   *
   * A new hue family in a palette that deliberately has few, so it needs its
   * reason stated. Levels have to read as a sibling of the streak callouts in a
   * different key — the same grammar, unmistakably not the same event — and
   * every existing colour here already means something specific and load-bearing:
   * gold is money and the streak ladder, violet is the wild card and the primary
   * action, mint is exactly 21, rose is a bust. Reusing any of them would make a
   * level-up look like a thing the player had scored, which is the one thing a
   * level is not.
   *
   * Cool and electric against a table lit gold and violet, so the two ceremonies
   * never compete even when they land on the same move.
   */
  azure: 0x4361ee,
  azureLight: 0x4cc9f0,

  white: 0xffffff,
  ink: 0x0a0710,
} as const;

/**
 * The nine rank colours, Pebble through Legend.
 *
 * A ramp rather than nine unrelated hues: cool stone at the bottom, through
 * this game's own violet and mint in the middle, into gold and hot at the top,
 * finishing on a white-gold that is deliberately the only near-white in the
 * whole palette. Read in order they should feel like heating metal, because
 * that is the one progression metaphor nobody needs explained.
 *
 * Shared in ORDER with the other titles, not in hex — Blockari's ramp is its
 * own stone-to-gold set. What has to match is that rank 3 is cooler than rank
 * 7 in both games, and that Legend is unmistakable in either.
 *
 * Four of these intentionally reuse existing palette entries. A rank is a
 * frame around a name, never a fill on the table, so it cannot collide with
 * the meanings those colours carry in play.
 */
export const rankPalette = [
  // Deuce is everybody's first five levels, so it has to read as a colour
  // rather than as a disabled control. The old #8A8FA3 sat close enough to
  // this UI's dim ink (white at 0.45) that the badge looked switched off —
  // lifted well clear of it.
  '#BFC8E2', // Deuce     — pale slate
  '#5B6BD6', // Seven     — indigo
  '#4CC9F0', // Ten       — sky (the level azure's bright end)
  '#34D399', // Jack      — mint
  '#A3E635', // Queen     — lime
  '#FFC53D', // King      — gold
  '#FB923C', // Ace       — orange
  '#F43F5E', // Blackjack — rose
  // White-gold, and deliberately nearer white than King's gold is: the top of
  // the ladder must not read as "gold again, slightly paler".
  '#FFF0B8', // Wild      — white-gold
] as const;

/** CSS-side mirror of the palette for the DOM HUD. */
export const cssPalette = {
  void: '#0D0A18',
  felt: '#1B1030',
  feltDeep: '#120A20',
  plum: '#2A1A4A',
  gold: '#FFC53D',
  goldDeep: '#C98A12',
  violet: '#B401E0',
  violetLight: '#D873FF',
  mint: '#34D399',
  rose: '#F43F5E',
  /** Levels — see the note on `palette.azure`. */
  azure: '#4361EE',
  azureLight: '#4CC9F0',
  white: '#FFFFFF',
  ink: '#0A0710',
} as const;

/**
 * Type, in two registers.
 *
 * `display` / `body` are the GAME's faces — Bebas Neue on the card faces and
 * pile totals, Saira where the canvas sets body copy. They ship with the repo
 * and they are part of the card art, so they stay.
 *
 * `shellDisplay` / `shellBody` are the SHELL's — every DOM screen: start,
 * history, profile, challenge, settings, the top bar. Rajdhani and Space
 * Grotesk, Blockari's faces, by Adam's decision (2026-09-19): the Chain Games
 * titles share one shell and it is easier in the long run if the shell's type
 * is one thing. Loaded from Google Fonts in index.html with the same weights
 * Blockari loads, so the metrics — and therefore the layout numbers the parity
 * critic measures — are the same files.
 *
 * The logo and the card art are artwork and carry no live type at all.
 */
export const type = {
  display: "'Bebas Neue', 'Oswald', sans-serif",
  body: "'Saira', system-ui, sans-serif",
  shellDisplay: "'Rajdhani', 'Space Grotesk', system-ui, sans-serif",
  shellBody: "'Space Grotesk', system-ui, sans-serif",
  /** Large display sizes need real tracking or they read as cramped. */
  displayTracking: '0.06em',
} as const;

/** Motion vocabulary. Shared so the whole app moves as one system. */
export const motion = {
  /** Card leaving the stock and arriving on a pile. */
  deal: 0.42,
  /** Card face flip. */
  flip: 0.34,
  /** Pile clearing after a bonus. */
  clear: 0.55,
  /** Bust — heavier, with a drop. */
  bust: 0.6,
  /** UI element fades. */
  ui: 0.24,
  /**
   * Springy settle used when a card lands.
   *
   * `elastic.out(1, 0.62)` over 0.5s has its first overshoot at t=0.465 with a
   * magnitude of 3.98%. Against the 5.5% squash it was applied to, that is
   * 0.22% of scale — **0.44px on a 200px card**. It was nominally elastic and
   * practically a damped ease. A shorter period gives about two visible
   * oscillations instead of one sub-pixel blip.
   */
  settle: { duration: 0.4, ease: 'elastic.out(1.1, 0.34)' },
  /** Default ease for cards travelling through space. */
  travel: 'power3.out',
  /** Anticipation ease for things that leave. */
  exit: 'power2.in',
} as const;

/** Elevation ramp — two steps, used consistently. */
export const elevation = {
  resting: { blur: 12, opacity: 0.45, y: 4 },
  lifted: { blur: 28, opacity: 0.55, y: 12 },
} as const;
